import React, { useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import type { CreateAIAgentRequest } from '../services/agentsApi';
import { useModelCredits, formatarCreditos } from '../hooks/useModelCredits';
import {
  getAgentModels,
  AI_MODELS,
  DEFAULT_AGENT_MODEL,
  type AIModelDefinition,
  type AIModelFeature,
} from '@troia-v3/types';

/**
 * Labels humanos para categorias e segmentos de agente (Sprint de Cenários).
 *
 * Definidos localmente em vez de importar `AGENT_CATEGORY_LABELS` /
 * `AGENT_SEGMENT_LABELS` do `@troia-v3/types` por 2 motivos:
 * 1. Evita bugs de bundling / HMR quando o types package é atualizado
 *    via `.tgz` local durante o desenvolvimento.
 * 2. Permite customização visual específica do frontend (emojis, ordem)
 *    sem precisar rebumbar o types package.
 *
 * As keys são os VALUES dos enums `AgentCategory` e `AgentSegment` do
 * types package — manter sincronizado se o enum mudar.
 */
const AGENT_CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'sdr', label: 'SDR (Prospecção)' },
  { value: 'attendant', label: 'Atendente' },
  { value: 'support', label: 'Suporte Técnico' },
  { value: 'financial', label: 'Financeiro' },
  { value: 'sales', label: 'Vendas' },
  { value: 'scheduling', label: 'Agendamento' },
  { value: 'other', label: 'Outro' },
];

const AGENT_SEGMENT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'educational', label: 'Educacional' },
  { value: 'real_estate', label: 'Imobiliária' },
  { value: 'healthcare', label: 'Saúde' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'services', label: 'Serviços' },
  { value: 'saas', label: 'SaaS / Software' },
  { value: 'accounting', label: 'Contabilidade' },
  { value: 'law', label: 'Advocacia' },
  { value: 'food', label: 'Alimentação' },
  { value: 'automotive', label: 'Automotivo' },
  { value: 'solar_energy', label: 'Energia Solar' },
  { value: 'apparel', label: 'Confecção' },
  { value: 'generic', label: 'Genérico' },
];

interface BasicTabProps {
  formData: Partial<CreateAIAgentRequest>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<CreateAIAgentRequest>>>;
}

/** Nome de exibição de cada fabricante do catálogo. */
const NOME_DO_FABRICANTE: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  deepseek: 'DeepSeek',
  zai: 'Z.ai',
  xai: 'xAI',
  mistral: 'Mistral',
  moonshotai: 'Moonshot (Kimi)',
  alibaba: 'Alibaba (Qwen)',
  minimax: 'MiniMax',
  meta: 'Meta (Llama)',
  microsoft: 'Microsoft',
};

export const BasicTab: React.FC<BasicTabProps> = ({ formData, setFormData }) => {
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const modelTriggerRef = useRef<HTMLButtonElement>(null);
  // TEMPORÁRIO (23/10/2026, decisão do dono): todos os agentes usam só o
  // GPT-6 Luna, então ele é o único modelo da lista. Para liberar os outros
  // de novo, volte para a linha comentada abaixo.
  // const viableModels = useMemo(() => getAgentModels(), []);
  const viableModels = useMemo(() => getAgentModels().filter((m) => m.id === DEFAULT_AGENT_MODEL), []);

  // Preço real deste app, por 1M de tokens. Vem do servidor porque o preço do
  // catálogo é em dólar (custo NOSSO) e o que o cliente paga está na tabela
  // de custos do app, editável no admin.
  const { data: precos } = useModelCredits();
  const precoPorModelo = useMemo(
    () => new Map((precos ?? []).map((p) => [p.modelId, p])),
    [precos],
  );

  /**
   * Selo de custo. Ausente quando o app não tem preço para a categoria — em
   * vez de mostrar zero, que o cliente leria como "de graça".
   */
  const SeloDeCredito: React.FC<{ modelId: string; claro?: boolean }> = ({ modelId, claro }) => {
    const preco = precoPorModelo.get(modelId);
    if (!preco || (preco.inputPerMillion === null && preco.outputPerMillion === null)) return null;
    const cor = claro ? '#6b7280' : 'var(--theme-muted-text)';
    const fundo = claro ? '#f3f4f6' : 'rgba(148,163,184,0.14)';
    return (
      <span
        className="px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
        style={{ backgroundColor: fundo, color: cor }}
        title="Créditos por 1 milhão de tokens — entrada (o que você envia) e saída (o que o modelo responde)"
      >
        {preco.inputPerMillion !== null ? formatarCreditos(preco.inputPerMillion) : '—'}
        {' / '}
        {preco.outputPerMillion !== null ? formatarCreditos(preco.outputPerMillion) : '—'}
        {' cr'}
      </span>
    );
  };

  const featureLabels: Record<AIModelFeature, string> = {
    pdf: 'PDF',
    image: 'Imagem',
    tools: 'Tools',
    reasoning: 'Reasoning',
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Identificação — card de seção do mockup: header (ícone tile + título + sub) DENTRO do card */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
      >
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
          <span
            className="flex items-center justify-center flex-none rounded-[10px]"
            style={{ width: 34, height: 34, backgroundColor: 'rgba(148,163,184,0.14)', color: 'var(--theme-muted-text)' }}
          >
            <Icon name="cpu" size={16} />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold" style={{ color: 'var(--theme-card-text)' }}>Identificação</h3>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Nome, categoria, segmento e descrição</p>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* row2 do mockup: Nome* | Categoria */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Nome do Agente *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Agente de Suporte Técnico"
                className="w-full h-[38px] px-3 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--theme-input-border)';
                }}
              />
            </div>
            <div>
              <label className="block text-[13px] font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Categoria
              </label>
              <select
                value={formData.category ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    category: (e.target.value || undefined) as typeof formData.category,
                  })
                }
                className="w-full h-[38px] px-3 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              >
                <option value="">Selecione uma categoria...</option>
                {AGENT_CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Define que tipo de função o agente exerce — usada para selecionar cenários de teste relevantes no treinamento.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
              Descrição
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o propósito deste agente..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-input-text)',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--theme-input-border)';
              }}
            />
          </div>

          {/* Segmento full-width (mockup) */}
          <div>
            <div>
              <label className="block text-[13px] font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Segmento de mercado
              </label>
              <select
                value={formData.segment ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    segment: (e.target.value || undefined) as typeof formData.segment,
                  })
                }
                className="w-full h-[38px] px-3 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              >
                <option value="">Selecione um segmento...</option>
                {AGENT_SEGMENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Indústria onde o agente atua — combinada com a categoria para refinar os cenários de teste.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modelo de IA — card de seção do mockup */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
      >
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
          <span
            className="flex items-center justify-center flex-none rounded-[10px]"
            style={{ width: 34, height: 34, backgroundColor: 'rgba(148,163,184,0.14)', color: 'var(--theme-muted-text)' }}
          >
            <Icon name="sliders" size={16} />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold" style={{ color: 'var(--theme-card-text)' }}>Modelo de IA</h3>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Provedor, temperatura e limites</p>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-[13px] font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
              Modelo *
            </label>
            {/* Model selector trigger */}
            <button
              ref={modelTriggerRef}
              type="button"
              onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
              className="w-full px-3 py-2.5 rounded-lg border text-sm text-left flex items-center justify-between"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-input-text)',
              }}
            >
              <div className="flex-1 min-w-0">
                {(() => {
                  const selected = viableModels.find(m => m.id === formData.model)
                    || AI_MODELS.find(m => m.id === formData.model);
                  if (!selected) return <span style={{ color: 'var(--theme-muted-text)' }}>Selecione um modelo</span>;
                  return (
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{selected.name}</div>
                        {/* Mockup `.mp-p`: PROVEDOR sob o nome (não o texto de marketing `highlight`) */}
                        <div className="text-xs truncate mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                          {NOME_DO_FABRICANTE[selected.provider] || selected.provider}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <SeloDeCredito modelId={selected.id} />
                        {selected.features.map(f => (
                          <span
                            key={f}
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                            style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
                          >
                            {featureLabels[f]}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <Icon name="chevron-down" className="h-4 w-4 flex-shrink-0 ml-2" style={{ color: 'var(--theme-muted-text)' }} />
            </button>

            {/* Model dropdown portal — hardcoded light theme, opens upward */}
            {modelDropdownOpen && createPortal(
              <>
                <div className="fixed inset-0 z-[100]" onClick={() => setModelDropdownOpen(false)} />
                <div
                  className="fixed z-[101] rounded-lg border shadow-lg overflow-y-auto"
                  style={{
                    backgroundColor: '#ffffff',
                    borderColor: '#e5e7eb',
                    maxHeight: '400px',
                    width: modelTriggerRef.current?.offsetWidth ?? 400,
                    bottom: window.innerHeight - (modelTriggerRef.current?.getBoundingClientRect().top ?? 0) + 4,
                    left: modelTriggerRef.current?.getBoundingClientRect().left ?? 0,
                  }}
                >
                  {Object.entries(
                    viableModels.reduce<Record<string, AIModelDefinition[]>>((acc, model) => {
                      const providerName = NOME_DO_FABRICANTE[model.provider] || model.provider;
                      if (!acc[providerName]) acc[providerName] = [];
                      acc[providerName].push(model);
                      return acc;
                    }, {})
                  ).map(([provider, models]) => (
                    <div key={provider}>
                      <div
                        className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider sticky top-0"
                        style={{
                          backgroundColor: '#ffffff',
                          color: '#6b7280',
                          borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        {provider}
                      </div>
                      {models.map(model => (
                        <button
                          key={model.id}
                          type="button"
                          className="w-full px-3 py-2.5 text-left transition-colors"
                          style={{
                            backgroundColor: formData.model === model.id ? '#f3f4f6' : 'transparent',
                            color: '#1f2937',
                          }}
                          onMouseEnter={e => {
                            if (formData.model !== model.id) {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }
                          }}
                          onMouseLeave={e => {
                            if (formData.model !== model.id) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                          onClick={() => {
                            setFormData({ ...formData, model: model.id });
                            setModelDropdownOpen(false);
                          }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium">{model.name}</div>
                              <div className="text-xs truncate mt-0.5" style={{ color: '#6b7280' }}>{model.highlight}</div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <SeloDeCredito modelId={model.id} claro />
                              {model.features.map(f => (
                                <span
                                  key={f}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                                  style={{ backgroundColor: '#f0f0f0', color: '#6b7280' }}
                                >
                                  {featureLabels[f]}
                                </span>
                              ))}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </>,
              document.body
            )}

            {/* O selo sozinho é críptico ("10 mil / 23 mil cr" — de quê?).
                Uma linha resolve, e some quando o app não tem preço. */}
            {precoPorModelo.size > 0 && (
              <p className="text-[11px] mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                Créditos por 1 milhão de tokens — entrada / saída.
              </p>
            )}
          </div>

          {/* Temperatura full-width (mockup): valor roxo à direita + slider roxo */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[13px] font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                Temperatura
              </label>
              <span className="text-[13px] font-semibold" style={{ color: 'var(--theme-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {formData.temperature}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={formData.temperature}
              onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
              className="w-full"
              style={{ accentColor: 'var(--theme-primary)' }}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
              0.0 = conservador, 1.0 = criativo
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Máximo de Tokens
              </label>
              <input
                type="number"
                value={formData.maxTokens}
                onChange={(e) => setFormData({ ...formData, maxTokens: parseInt(e.target.value) })}
                className="w-full h-[38px] px-3 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              />
            </div>

            <div>
              <label className="block text-[13px] font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
                Delay (segundos)
              </label>
              <input
                type="number"
                min={10}
                max={120}
                value={formData.delay ?? 12}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val)) {
                    setFormData({ ...formData, delay: Math.min(120, Math.max(10, val)) });
                  }
                }}
                className="w-full h-[38px] px-3 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
